var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix4, Quaternion, Vector3 } from "three";
import { AskErrorMessage, CollisionFacet, CollisionShapeLocalTransformFacet, EnterRoomMessage, FrameStatsMessage, isCompoundShape, PrimitiveShape, RequestFrameStatsMessage, RoomChangedMessage } from "flavr-common";
import { Box3 } from "flavr-math";
import { CollisionShapePrimitive, CreateCollisionShapeMessage, OnEnterContactsMessage, OnLeaveContactsMessage, RemoveAllCollisionShapesMessage, RemoveCollisionShapeMessage, RequestCollisionShapeAABBMessage, RequestCollisionShapeGeometryMessage, RequestCollisionShapeLocalTransformMessage, RequestIntersectionsWithCollisionShapeMessage, RequestIntersectionsWithPrimitiveCollisionShapeMessage, RequestRaycastClosestIntersectionMessage, ResponseCollisionShapeAABBMessage, ResponseCollisionShapeGeometryMessage, ResponseCollisionShapeLocalTransformMessage, ResponseIntersectionsWithCollisionShapeMessage, ResponseIntersectionsWithPrimitiveCollisionShapeMessage, ResponseRaycastClosestIntersectionMessage, UpdateCollisionShapeLocalTransformMessage, UpdateCollisionShapeSelectableStateMessage } from "@messages/selection";
import { EndSessionMessage } from "@messages/session";
import { FrameStats } from "@messages/rendering";
import { FacetHandler, FacetRemoveHandler, MessageHandler, MessageRequestHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { inputActionService } from "@modules/inputs";
import { getModelNameByUID, modelPool } from "@modules/model";
import { collisionDebugService } from "./CollisionDebugService";
import { CollisionWorld, ContactType } from "./CollisionWorld";
import { CollisionShape } from "./CollisionShape";
import { CompoundColliderManager } from "./CompoundColliderManager";
/**
 * This module handles selection of models that have a collision shape. The user can specify a custom collision
 * shape to simplify the selection process. This module supports ray-mesh as well as mesh-mesh intersection tests. It
 * also checks each frame if two collision shapes entered or left an intersection and broadcasts
 * `OnEnterContactsMessage` as well as `OnLeaveContactsMessage` if anything has changed since last frame.
 *
 * Facets:
 * - `CollisionFacet` - Defines a model's collision shape and whether it is selectable and/or a trigger. Compound
 *   shapes are registered with the `CompoundColliderManager` instead of being built directly, since their elements
 *   are derived live from descendant models rather than baked into the facet.
 * - `CollisionShapeLocalTransformFacet` - Offsets a collision shape from its model's own transform.
 *
 * Messages:
 * - `EnterRoomMessage` and `RoomChangedMessage` - Enter, activate and deactivate the `CollisionRoom` for the
 *   respective room so collision shapes are tracked and queried per room.
 * - `CreateCollisionShapeMessage`, `RemoveCollisionShapeMessage` and `RemoveAllCollisionShapesMessage`/`EndSessionMessage`
 *   - Create, remove or clear collision shapes outside of the facet lifecycle.
 * - `UpdateCollisionShapeSelectableStateMessage` - Changes whether a collision shape is selectable and/or a trigger.
 * - `UpdateCollisionShapeLocalTransformMessage` - Updates a collision shape's local transform; deferred until the
 *   shape has been created if it doesn't exist yet.
 * - `RequestCollisionShapeLocalTransformMessage`, `RequestCollisionShapeGeometryMessage`,
 *   `RequestCollisionShapeAABBMessage` and `RequestFrameStatsMessage` - Answer queries about a collision shape's
 *   local transform, geometry, bounds or query performance stats.
 * - `RequestRaycastClosestIntersectionMessage`, `RequestIntersectionsWithPrimitiveCollisionShapeMessage` and
 *   `RequestIntersectionsWithCollisionShapeMessage` - Perform ray casts and intersection tests against active
 *   collision shapes in the current room and the utility room.
 *
 * Events:
 * - `ModelEventType.MODEL_DESTROYED` - Removes the model's collision shape and unregisters it from the
 *   `CompoundColliderManager`.
 * - `ModelEventType.TRANSFORM_UPDATED` - Updates the collision shape's world transform.
 * - `ModelEventType.ROOM_CHANGED` - Moves the collision shape to its model's new room.
 * - `ModelEventType.PARENT_CHANGED` - Refreshes the collision shape's room and world transform after reparenting.
 *
 * Structure:
 * The `CollisionWorld` holds all `CollisionRoom`s, and each `CollisionRoom` holds the `CollisionShape`s of the models
 * that currently reside in it. Ray casts and intersection tests are usually only interested in the current room and
 * the always-persistent utility room, so each room keeps its own `CollisionShapeContainer` and, while a room is
 * active, its container is combined with the utility room's container into a shared container. The
 * `CollisionShapeContainer` is what makes this fast: switching rooms just swaps which containers are combined,
 * rather than moving individual collision shapes around.
 *
 * A `CollisionShape` can be selectable, a trigger, both or neither, depending on its `isSelectable` and `isTrigger`
 * flags. `isSelectable` means the user can select the shape (e.g. by clicking or pointing at it). `isTrigger` means
 * the shape is not selectable but still takes part in the per-frame collision checks (and can therefore still send
 * enter/leave contact events). If both are off, the collision shape is ignored by ray casts and intersection tests
 * that don't specify explicit target model uids - however, a collision shape can always be tested directly by
 * specifying its model's uid, regardless of its selectable/trigger state.
 *
 * The `CompoundColliderManager` handles models whose `CollisionFacet` describes a compound shape. Since a compound
 * shape's elements are derived from the collision shapes of a model's descendants rather than being baked in
 * upfront, the manager recomputes and rebuilds the compound shape whenever a relevant descendant, or the parent-child
 * relationships beneath it, changes.
 */
export class CollisionModule extends NewModule {
    /**
     * Sets up the `CollisionWorld` and `CompoundColliderManager`, and registers this module's collider state with the
     * `collisionDebugService` so the debug UI can inspect all rooms and collision shapes.
     */
    constructor() {
        super("CollisionModule");
        this.isActive = true;
        // Pending LocalTransformFacets
        this.pendingLocalTransformFacets = new Map();
        /// Collision contact helper method ///
        /**
         * Checks each frame if collision shapes entered or left a collision at that frame. Besides including active collision
         * shapes, we also consider collision shapes that are triggers.
         */
        this.updateCollisionContacts = () => {
            if (!this.isActive)
                return;
            const changedContacts = this.collisionWorld.updateCollisionContacts();
            // Collect enter and leave contact events
            const onEnterContacts = [];
            const onLeaveContacts = [];
            for (const contactInfo of changedContacts) {
                const contact = {
                    collisionShapeAUID: contactInfo.collisionShapeAUID,
                    collisionShapeBUID: contactInfo.collisionShapeBUID
                };
                if (contactInfo.contactType === ContactType.ENTER) {
                    onEnterContacts.push(contact);
                }
                else if (contactInfo.contactType === ContactType.LEAVE)
                    onLeaveContacts.push(contact);
            }
            const messages = [];
            if (onEnterContacts.length > 0)
                messages.push(new OnEnterContactsMessage(onEnterContacts));
            if (onLeaveContacts.length > 0)
                messages.push(new OnLeaveContactsMessage(onLeaveContacts));
            if (messages.length > 0)
                this.connection.sendMultiple(messages);
        };
        this.collisionWorld = new CollisionWorld();
        this.compoundColliderManager = new CompoundColliderManager((uid, elements) => {
            void this.applyCompoundElements(uid, elements);
        });
        this.queryFrameStats = new FrameStats();
        collisionDebugService.setSnapshotProvider(() => ({
            rooms: this.collisionWorld.getDebugSnapshot()
        }));
    }
    /// Life cycle methods ///
    /**
     * Registers the dev-only key bindings for toggling collision shape visibility (`KeyP`) and hiding inactive
     * colliders (`KeyL`).
     */
    onInit() {
        inputActionService
            .registerAction("ToggleColliderVisibility", () => {
            this.collisionWorld.toggleCollisionShapesVisibility();
        }, "Show and hide selection geometry")
            .addKey("KeyP")
            .asDev();
        inputActionService
            .registerAction("ToggleInactiveColliderVisibility", () => {
            this.collisionWorld.toggleHideInactiveColliders();
        }, "Show and hide inactive selection geometry")
            .addKey("KeyL")
            .asDev();
    }
    onActivate() {
        this.isActive = true;
    }
    onDeactivate() {
        this.isActive = false;
    }
    /**
     * Runs the per-frame collision contact check and broadcasts any `OnEnterContactsMessage`/`OnLeaveContactsMessage`
     * that result.
     */
    onPreRender() {
        this.updateCollisionContacts();
    }
    /// Room message handlers ///
    handleEnterRoomMessage(message) {
        const nextRoomUID = message.uid;
        this.collisionWorld.enterNextRoom(nextRoomUID);
    }
    handleRoomChangedMessage(message) {
        if (message.prevUid !== undefined)
            this.collisionWorld.leaveRoom(message.prevUid);
        this.collisionWorld.activateCurrentRoom(message.uid);
    }
    /// Collision shape message handlers ///
    handleCollisionFacet(uid, facet) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isCompoundShape(facet.shape)) {
                // Compound shapes aren't baked into the facet - they're derived live from descendant CollisionFacets and kept
                // up to date as children are attached/removed (see CompoundColliderManager)
                this.compoundColliderManager.register(uid);
                return;
            }
            if (this.compoundColliderManager.isRegistered(uid))
                this.compoundColliderManager.unregister(uid);
            yield this.collisionWorld.updateOrCreateCollisionShape(uid, facet.shape, facet.isSelectable, facet.isTrigger);
            // TODO: This is a bit ugly and we only take care of CollisionShapeLocalTransformFacets other facets might not be
            //  applied correctly if the collision shape is not yet resolved properly. Maybe come up with a mechanism that checks
            //  if a collision shape is currently being created and if its not done defers the other facets and messages to when
            //  the collision shape is done
            // Collision facets are handled asynchronously and can take longer than the local transform facets
            this.resolvePendingTransformFacet(uid);
        });
    }
    handleRemoveFacetFromModelMessage(uid, _facet) {
        this.compoundColliderManager.unregister(uid);
        this.collisionWorld.removeCollisionShape(uid);
    }
    /// Compound collider helper method ///
    applyCompoundElements(uid, elements) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const facet = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(CollisionFacet);
            if (facet === undefined)
                return;
            if (elements.length === 0) {
                this.collisionWorld.removeCollisionShape(uid);
                return;
            }
            yield this.collisionWorld.updateOrCreateCollisionShape(uid, elements, facet.isSelectable, facet.isTrigger);
            this.resolvePendingTransformFacet(uid);
        });
    }
    handleCreateCollisionShapeMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.collisionWorld.updateOrCreateCollisionShape(message.uid, message.shape, message.isSelectable, message.isTrigger);
            this.resolvePendingTransformFacet(message.uid);
        });
    }
    handleRemoveCollisionShapeMessage(message) {
        this.collisionWorld.removeCollisionShape(message.uid);
    }
    handleModelDestroyedEvent(model, _destroyPermanently) {
        this.compoundColliderManager.unregister(model.getUID());
        this.collisionWorld.removeCollisionShape(model.getUID());
    }
    handleRemoveAllCollisionShapesMessage(_) {
        this.collisionWorld.removeAllCollisionShapes();
    }
    handleCollisionShapesTransformUpdate(model, _localTransform) {
        this.collisionWorld.updateCollisionShapeTransform(model.getUID(), model.getWorldTransform());
    }
    handleCollisionShapeRoomChanged(model) {
        this.collisionWorld.refreshCollisionShapeRoom(model.getUID());
    }
    handleCollisionShapeParentChanged(model) {
        this.collisionWorld.refreshCollisionShapeRoom(model.getUID());
        this.collisionWorld.updateCollisionShapeTransform(model.getUID(), model.getWorldTransform());
    }
    handleEnableCollisionShapeMessage(message) {
        this.collisionWorld.updateCollisionShapeSelectableState(message.uid, message.isSelectable, message.isTrigger);
    }
    /// Collision shape local transform handlers
    handleUpdateCollisionShapeLocalTransformMessage(message) {
        // We might need to wait for the collision shape to be resolved first since this happens asynchronously
        if (this.collisionWorld.getCollisionShape(message.uid) === undefined) {
            this.pendingLocalTransformFacets.set(message.uid, new CollisionShapeLocalTransformFacet(message.localTransform));
            return;
        }
        this.collisionWorld.updateCollisionShapeLocalTransform(message.uid, new Matrix4().fromArray(message.localTransform));
    }
    handleUpdateCollisionShapeLocalTransformFacet(uid, facet) {
        // We might need to wait for the collision shape to be resolved first since this happens asynchronously
        if (this.collisionWorld.getCollisionShape(uid) === undefined) {
            this.pendingLocalTransformFacets.set(uid, facet);
            return;
        }
        this.collisionWorld.updateCollisionShapeLocalTransform(uid, new Matrix4().fromArray(facet.localTransform));
    }
    handleRemoveUpdateCollisionShapeLocalTransformFacet(uid, _facet) {
        this.collisionWorld.updateCollisionShapeLocalTransform(uid, new Matrix4());
    }
    handleRequestCollisionShapeLocalTransformMessage(message, askId) {
        if (askId === undefined) {
            console.error("[collision] Received a RequestCollisionShapeLocalTransformMessage without an askId");
            return;
        }
        const collisionShape = this.collisionWorld.getCollisionShape(message.uid);
        if (collisionShape === undefined) {
            this.connection.answer(askId, new AskErrorMessage(`There is no collision shape for ${getModelNameByUID(message.uid)}`));
            return;
        }
        this.connection.answer(askId, new ResponseCollisionShapeLocalTransformMessage(collisionShape.localTransform.toArray()));
    }
    /**
     * Returns the vertex/index data of whatever geometry is actually committed for this uid right now - for a compound
     * shape this is the live-merged geometry built from CompoundColliderManager's derived elements, not whatever the
     * requester's own (possibly unresolved) CollisionFacet mirror shows.
     */
    handleRequestCollisionShapeGeometryMessage(message, askId) {
        if (askId === undefined) {
            console.error("[collision] Received a RequestCollisionShapeGeometryMessage without an askId");
            return;
        }
        const collisionShape = this.collisionWorld.getCollisionShape(message.uid);
        if (collisionShape === undefined) {
            this.connection.answer(askId, new AskErrorMessage(`There is no collision shape for ${getModelNameByUID(message.uid)}`));
            return;
        }
        const position = collisionShape.geometry.getAttribute("position");
        const vertices = position !== undefined ? Array.from(position.array) : [];
        const index = collisionShape.geometry.index !== null ? Array.from(collisionShape.geometry.index.array) : [];
        this.connection.answer(askId, new ResponseCollisionShapeGeometryMessage(vertices, index));
    }
    /// Ray casting message handlers ///
    /**
     * Ray casts are only performed for active collision shapes.
     */
    handleRequestRaycastClosestIntersectionMessage(message, askId) {
        var _a, _b, _c, _d;
        if (!this.isActive) {
            this.connection.send(new ResponseRaycastClosestIntersectionMessage(undefined), askId);
            return;
        }
        this.queryFrameStats.begin();
        const raycastResult = this.collisionWorld.performRaycast(new Vector3().fromArray(message.origin), new Vector3().fromArray(message.direction), message.params.targetModelUIDs, (_b = (_a = message.params) === null || _a === void 0 ? void 0 : _a.backfaceCulling) !== null && _b !== void 0 ? _b : false, (_d = (_c = message.params) === null || _c === void 0 ? void 0 : _c.includeTriggers) !== null && _d !== void 0 ? _d : false);
        this.queryFrameStats.end();
        this.connection.send(new ResponseRaycastClosestIntersectionMessage(raycastResult), askId);
    }
    /**
     * Checks for intersections between a primitive shape (e.g. a ray cone) and all active collision shapes in the current
     * room and the utility room.
     * @param message
     * @param askId
     */
    handleRequestIntersectionsWithPrimitiveCollisionShapeMessage(message, askId) {
        var _a, _b;
        if (!this.isActive) {
            this.connection.send(new ResponseIntersectionsWithCollisionShapeMessage([]), askId);
            return;
        }
        const shape = message.volume === CollisionShapePrimitive.SPHERE ? PrimitiveShape.Sphere : PrimitiveShape.Cone;
        const position = new Vector3().fromArray(message.position);
        const rotation = new Quaternion().fromArray(message.rotation);
        // SphereGeometry/ConeGeometry primitives have radius 0.5, so multiply by 2 to make maxDistance the actual radius
        const geometryRadiusFactor = 2;
        const scale = new Vector3(geometryRadiusFactor * message.scale * ((_a = message.maxConeRadius) !== null && _a !== void 0 ? _a : message.maxDistance), geometryRadiusFactor * message.scale * ((_b = message.maxConeRadius) !== null && _b !== void 0 ? _b : message.maxDistance), geometryRadiusFactor * message.scale * message.maxDistance);
        // Create selection geometry used to intersect the scene
        const testCollisionShape = new CollisionShape();
        testCollisionShape
            .create(-1, shape)
            .then(() => {
            testCollisionShape.position.set(position.x, position.y, position.z);
            testCollisionShape.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
            testCollisionShape.scale.set(scale.x, scale.y, scale.z);
            testCollisionShape.updateMatrixWorld(true);
            // We only intersect with objects that are enabled
            this.queryFrameStats.begin();
            const intersections = this.collisionWorld.performIntersection(testCollisionShape, undefined, message.computeHitpoint);
            this.queryFrameStats.end();
            this.connection.send(new ResponseIntersectionsWithPrimitiveCollisionShapeMessage(intersections), askId);
        })
            .catch((e) => {
            console.error("[selection]", e);
            this.connection.send(new ResponseIntersectionsWithCollisionShapeMessage([]), askId);
        })
            .finally(() => {
            testCollisionShape.destroy();
        });
    }
    /**
     * Checks for intersections between a test collision shape and target collision shapes that can be specified. If no
     * target collision shapes are specified then all active collision shapes in the current and utility room are
     * considered.
     * @param message
     * @param askId
     */
    handleRequestIntersectionsWithCollisionShapeMessage(message, askId) {
        if (!this.isActive) {
            this.connection.send(new ResponseIntersectionsWithPrimitiveCollisionShapeMessage([]), askId);
            return;
        }
        // Retrieve collision shape for test mesh
        const testCollisionShape = this.collisionWorld.getCollisionShape(message.testModelUID);
        if (testCollisionShape === undefined) {
            this.connection.send(new ResponseIntersectionsWithCollisionShapeMessage([]), askId);
            return;
        }
        this.queryFrameStats.begin();
        const intersections = this.collisionWorld.performIntersection(testCollisionShape, message.targetModelUIDs);
        this.queryFrameStats.end();
        this.connection.send(new ResponseIntersectionsWithPrimitiveCollisionShapeMessage(intersections), askId);
    }
    /// AABB message handler ///
    // ToDo: Hack we provide this handler since the scene graph aabb is broken for now
    handleRequestCollisionShapeAABBMessage(message, askId) {
        if (askId === undefined)
            return;
        const collider = this.collisionWorld.getCollisionShape(message.uid);
        if (collider === undefined) {
            this.connection.answer(askId, new AskErrorMessage(`Model ${getModelNameByUID(message.uid)} has no collision shape`));
            return;
        }
        const tempQuaternion = new Quaternion();
        if (message.ignoreRotation) {
            tempQuaternion.copy(collider.quaternion);
            collider.quaternion.identity();
            collider.updateWorldMatrix(false, false);
        }
        const aabb = new Box3().setFromObject(collider, true);
        if (message.ignoreRotation) {
            collider.quaternion.copy(tempQuaternion);
            collider.updateWorldMatrix(false, false);
        }
        const min = aabb.min.toArray();
        const max = aabb.max.toArray();
        this.connection.answer(askId, new ResponseCollisionShapeAABBMessage(min, max));
    }
    /// Pending local transform facets helper ///
    resolvePendingTransformFacet(uid) {
        const localTransformFacet = this.pendingLocalTransformFacets.get(uid);
        if (localTransformFacet === undefined)
            return;
        this.handleUpdateCollisionShapeLocalTransformFacet(uid, localTransformFacet);
        this.pendingLocalTransformFacets.delete(uid);
    }
    /// Frame stats handler ///
    handleRequestFrameStatsMessage(message, askId) {
        this.connection.send(new FrameStatsMessage(message.id, this.queryFrameStats.getFPS(), this.queryFrameStats.getFrameTime(), this.queryFrameStats.getAvgTime(), this.queryFrameStats.getWorstTime(), this.queryFrameStats.getBestTime()), askId);
    }
}
__decorate([
    MessageHandler(EnterRoomMessage)
], CollisionModule.prototype, "handleEnterRoomMessage", null);
__decorate([
    MessageHandler(RoomChangedMessage)
], CollisionModule.prototype, "handleRoomChangedMessage", null);
__decorate([
    FacetHandler(CollisionFacet)
], CollisionModule.prototype, "handleCollisionFacet", null);
__decorate([
    FacetRemoveHandler(CollisionFacet)
], CollisionModule.prototype, "handleRemoveFacetFromModelMessage", null);
__decorate([
    MessageHandler(CreateCollisionShapeMessage)
], CollisionModule.prototype, "handleCreateCollisionShapeMessage", null);
__decorate([
    MessageHandler(RemoveCollisionShapeMessage)
], CollisionModule.prototype, "handleRemoveCollisionShapeMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], CollisionModule.prototype, "handleModelDestroyedEvent", null);
__decorate([
    MessageHandler(RemoveAllCollisionShapesMessage, EndSessionMessage)
], CollisionModule.prototype, "handleRemoveAllCollisionShapesMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], CollisionModule.prototype, "handleCollisionShapesTransformUpdate", null);
__decorate([
    ModelEventHandler(ModelEventType.ROOM_CHANGED)
], CollisionModule.prototype, "handleCollisionShapeRoomChanged", null);
__decorate([
    ModelEventHandler(ModelEventType.PARENT_CHANGED)
], CollisionModule.prototype, "handleCollisionShapeParentChanged", null);
__decorate([
    MessageHandler(UpdateCollisionShapeSelectableStateMessage)
], CollisionModule.prototype, "handleEnableCollisionShapeMessage", null);
__decorate([
    MessageHandler(UpdateCollisionShapeLocalTransformMessage)
], CollisionModule.prototype, "handleUpdateCollisionShapeLocalTransformMessage", null);
__decorate([
    FacetHandler(CollisionShapeLocalTransformFacet)
], CollisionModule.prototype, "handleUpdateCollisionShapeLocalTransformFacet", null);
__decorate([
    FacetRemoveHandler(CollisionShapeLocalTransformFacet)
], CollisionModule.prototype, "handleRemoveUpdateCollisionShapeLocalTransformFacet", null);
__decorate([
    MessageRequestHandler(RequestCollisionShapeLocalTransformMessage)
], CollisionModule.prototype, "handleRequestCollisionShapeLocalTransformMessage", null);
__decorate([
    MessageRequestHandler(RequestCollisionShapeGeometryMessage)
], CollisionModule.prototype, "handleRequestCollisionShapeGeometryMessage", null);
__decorate([
    MessageRequestHandler(RequestRaycastClosestIntersectionMessage)
], CollisionModule.prototype, "handleRequestRaycastClosestIntersectionMessage", null);
__decorate([
    MessageRequestHandler(RequestIntersectionsWithPrimitiveCollisionShapeMessage)
], CollisionModule.prototype, "handleRequestIntersectionsWithPrimitiveCollisionShapeMessage", null);
__decorate([
    MessageRequestHandler(RequestIntersectionsWithCollisionShapeMessage)
], CollisionModule.prototype, "handleRequestIntersectionsWithCollisionShapeMessage", null);
__decorate([
    MessageRequestHandler(RequestCollisionShapeAABBMessage)
], CollisionModule.prototype, "handleRequestCollisionShapeAABBMessage", null);
__decorate([
    MessageRequestHandler(RequestFrameStatsMessage)
], CollisionModule.prototype, "handleRequestFrameStatsMessage", null);
